<script setup lang="ts">
import { computed, h, onMounted, ref, watch, type VNode } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import {
  NIcon,
  NSplit,
  useThemeVars,
  type DataTableColumns,
  type DataTableSortState,
} from 'naive-ui';
import { IconClose, IconEdit, IconPlayCircle } from '@/components/design/icons';
import {
  type ListPlaybookEvaluationsResponse,
  type PlaybookEvaluationGroupInDb,
  type PlaybookRunInDb,
  type VaultFileRevisionInDb,
} from '@/api';
import { PlaybooksService } from '@/services/playbooks/playbooksService';
import {
  evaluationGroupBuckets,
  evaluationTypeBuckets,
  filterEvaluationColumns,
  showGroupFilterRow,
  usePlaybookRunCells,
} from '@/composables/usePlaybookRunCells';
import {
  actionResultDetail,
  actionResultTypeLabel,
  actionTypeIcon,
  averageFindingBucket,
  buildOptionMap,
  clauseOptionTypeShortLabel,
  evaluationConfiguredActions,
  extractedValue,
  extractionResponseType,
  firedEvaluationActions,
  groupsByTriggerUid,
  isExtractionResult,
  isUncommonsResult,
  latestResultForEvaluation,
  resultSeverity,
  runInputText,
  startedByInitials,
  startedByLabel,
  stepStatusTagType,
  uncommonsFindings,
  type PlaybookActionResult,
  type PlaybookEvaluation,
  type SeverityBucket,
  type TagType,
} from '@/services/playbooks/playbookRunDisplay';
import { formatDisplayValue } from '@/utils/displayAiResponse';
import { useVaultUploadDialog } from '@/composables/useVaultUploadDialog';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaAvatar from '@/components/design/base/GaiaAvatar.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCountLabel from '@/components/design/base/GaiaCountLabel.vue';
import GaiaDocPager from '@/components/design/composite/GaiaDocPager.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaPopconfirm from '@/components/design/composite/GaiaPopconfirm.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaRunFocus from '@/components/design/playbook/GaiaRunFocus.vue';
import GaiaRunListItem from '@/components/design/playbook/GaiaRunListItem.vue';
import GaiaRunsTable from '@/components/design/playbook/GaiaRunsTable.vue';
import GaiaRunResultDetail, {
  type GaiaResultAction,
} from '@/components/design/playbook/GaiaRunResultDetail.vue';
import GaiaRunResultRow, {
  type GaiaResultTone,
  type GaiaTriggerState,
} from '@/components/design/playbook/GaiaRunResultRow.vue';
import GaiaSectionHeader from '@/components/design/base/GaiaSectionHeader.vue';
import GaiaSegmented, {
  type GaiaSegmentedOption,
} from '@/components/design/base/GaiaSegmented.vue';
import GaiaSortControl, {
  type GaiaSortDirection,
  type GaiaSortOption,
} from '@/components/design/composite/GaiaSortControl.vue';
import { type GaiaChipTone } from '@/components/design/base/GaiaStatusChip.vue';
import GaiaStatusIndicator from '@/components/design/base/GaiaStatusIndicator.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import { playbookRunStatusMap } from '@/components/design/statusMaps';
import { gaiaAccent, gaiaContentWidth } from '@/components/design/themes/tokens';
import GaiaFileDropper from '@/components/file-dropper/GaiaFileDropper.vue';
import EvaluationColumnPicker from '@/components/playbooks/EvaluationColumnPicker.vue';
import PlaybookRunInputCell from '@/components/playbooks/PlaybookRunInputCell.vue';
import PlaybookStartRunModal from '@/components/playbooks/PlaybookStartRunModal.vue';
import GaiaVaultFilePdfPreview from '@/components/design/domain/fileVault/GaiaVaultFilePdfPreview.vue';
import type { FileDropAction } from '@/components/file-dropper/actions/types';
import { usePlaybookRunWebSocket } from '@/composables/usePlaybookRunWebSocket';
import { useListQuerySync, usePaginationConfig } from '@/composables/useListQuerySync';
import { useAuthStore } from '@/stores/auth';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { reviewProjectRoute, playbookEditRoute } from '@/router/links';

const route = useRoute();
const router = useRouter();
const message = useGaiaToast();
const { openUploadDialog } = useVaultUploadDialog();
const themeVars = useThemeVars();
const authStore = useAuthStore();

const playbookUid = computed<string>(() => {
  const uid = route.params.uid;
  return typeof uid === 'string' ? uid : '';
});
const canEditPlaybook = computed<boolean>(() => authStore.userPermissions.can_create_playbooks);

const runs = ref<PlaybookRunInDb[]>([]);
const evaluations = ref<ListPlaybookEvaluationsResponse>([]);
const evaluationGroups = ref<PlaybookEvaluationGroupInDb[]>([]);

// Which evaluation columns are shown. Both rows are single-select, as the mock
// draws them, so each ref holds one bucket key (or 'all') and feeds the
// multi-select shape `filterEvaluationColumns` expects.
const typeFilter = ref<string>('all');
const groupFilter = ref<string>('all');
const selectedTypes = computed<string[]>(() =>
  typeFilter.value === 'all' ? [] : [typeFilter.value]
);
const selectedGroups = computed<string[]>(() =>
  groupFilter.value === 'all' ? [] : [groupFilter.value]
);
// Individually unticked columns, on top of the two bucket filters. Local: which
// columns a reader wants is about this visit, not about the playbook.
const hiddenColumns = ref<string[]>([]);
const typeBuckets = computed(() => evaluationTypeBuckets(evaluations.value));
const groupBuckets = computed(() =>
  evaluationGroupBuckets(evaluations.value, evaluationGroups.value)
);
const visibleEvaluations = computed<ListPlaybookEvaluationsResponse>(() =>
  filterEvaluationColumns(evaluations.value, evaluationGroups.value, {
    types: selectedTypes.value,
    groups: selectedGroups.value,
    hidden: hiddenColumns.value,
  })
);

/** The mock's type chips: "All" plus one per non-empty type, with its column count. */
const typeSwitcherOptions = computed<GaiaSegmentedOption[]>(() => [
  { label: 'All', value: 'all' },
  ...typeBuckets.value.map(bucket => ({
    label: bucket.label,
    value: bucket.key,
    count: bucket.count,
  })),
]);

/** The same row for groups: "All" plus one per group that covers a visible column. */
const groupSwitcherOptions = computed<GaiaSegmentedOption[]>(() => [
  { label: 'All', value: 'all' },
  ...groupBuckets.value.map(bucket => ({
    label: bucket.label,
    value: bucket.key,
    count: bucket.count,
  })),
]);

const evalSummary = computed<string>(() => {
  const total = evaluations.value.length;
  if (total === 0) return '';
  const shown = visibleEvaluations.value.length;
  return shown === total
    ? `Showing all ${total} evaluations`
    : `Showing ${shown} of ${total} evaluations`;
});

const VIEW_OPTIONS: GaiaSegmentedOption[] = [
  { label: 'Per run', value: 'focus' },
  { label: 'Grid', value: 'grid' },
];

// The grid keeps the viewport-height split it has always used; the chrome above
// it is now the 56px topbar plus the shell's own top padding, so the offset
// grew from the old 24px-padded layout.
const splitHeight = computed<string>(() => 'calc(100vh - 340px)');

// The focus card fills the same band the grid does, so switching views doesn't
// move the page. `flex: none` because the card's own `flex: 1` (basis 0) in this
// auto-height column sizes it to its content and ignores the height, so the
// rail grows with its runs instead of scrolling.
const focusHeightStyle = computed(() => ({ height: splitHeight.value, flex: 'none' }));

/**
 * With no runs there is nothing for either view to lay out, so the page drops
 * to the empty state on its own — a grid of column headers over a blank body
 * describes a shape the user has not made yet.
 */
const showEmptyState = computed<boolean>(() => !loading.value && runs.value.length === 0);

/**
 * Shared label column for the two filter rows, so "Type" and
 * "Groups" start their switchers on the same x instead of each
 * sitting flush against a label of its own width. `minWidth`, not `width`: a
 * longer label loses the alignment rather than being clipped by it.
 */
const filterLabelStyle = { minWidth: '176px', flex: 'none' } as const;

/** The grid still draws while the first fetch is in flight, for its loading mask. */
const showGrid = computed<boolean>(() =>
  runs.value.length > 0 ? activeTab.value === 'grid' : loading.value
);

const { buildEvaluationColumnGroups, renderGroupActionsCell } = usePlaybookRunCells(
  visibleEvaluations,
  { evaluationGroups }
);
const totalCount = ref<number>(0);
const loading = ref<boolean>(false);
const error = ref<string | null>(null);
const playbookName = ref<string>('');

const { currentPage, pageSize, activeTab } = useListQuerySync({
  defaultTab: 'focus',
  allowedTabs: ['focus', 'grid'],
});

/** Rail sort. 'input' is deliberately absent — a run's input is either a vault
    file or raw text, so the backend has no single column to order it by. */
const RUN_SORTS: GaiaSortOption[] = [
  { key: 'created_at', label: 'Created' },
  { key: 'status', label: 'Status' },
];
const runSortKey = ref<string>('created_at');
const runSortDirection = ref<GaiaSortDirection>('desc');

function onRunSortKeyChange(key: string): void {
  runSortKey.value = key;
  void loadRuns();
}

function onRunSortDirectionChange(direction: GaiaSortDirection): void {
  runSortDirection.value = direction;
  void loadRuns();
}

/**
 * The grid's headers sort the same two refs the rail's control writes, so the
 * order survives a switch between the views instead of each holding its own.
 * `false` on every column but the sorted one — naive needs the full state to
 * take the columns as controlled.
 */
function sortOrderFor(key: string): 'ascend' | 'descend' | false {
  if (runSortKey.value !== key) return false;
  return runSortDirection.value === 'asc' ? 'ascend' : 'descend';
}

function onSorterChange(sorter: DataTableSortState | DataTableSortState[] | null): void {
  const next = Array.isArray(sorter) ? sorter[0] : sorter;
  // A third click on a header clears naive's sort, but the rail's control has
  // no unsorted state to show for it and the list still needs an order — so
  // "no sort" lands back on the newest-first default the page opens with.
  if (!next || next.order === false) {
    runSortKey.value = 'created_at';
    runSortDirection.value = 'desc';
  } else {
    runSortKey.value = String(next.columnKey);
    runSortDirection.value = next.order === 'ascend' ? 'asc' : 'desc';
  }
  void loadRuns();
}

/** The run whose results the pane shows — the first one until something is picked. */
const focusUid = ref<string | null>(null);
const focusRun = computed<PlaybookRunInDb | null>(() => {
  if (runs.value.length === 0) return null;
  return runs.value.find(run => run.uid === focusUid.value) ?? runs.value[0];
});
const starting = ref<boolean>(false);
const showStartRunModal = ref<boolean>(false);

const showTextModal = ref<boolean>(false);
const textModalContent = ref<string>('');

const previewRevisionUid = ref<string | null>(null);
const previewRevisionStatus = ref<string | null>(null);
const previewFileName = ref<string>('');
// NSplit size = fraction taken by the table pane; 1 means the preview is closed.
const splitSize = ref<number>(1);

function openTextModal(text: string): void {
  textModalContent.value = text;
  showTextModal.value = true;
}

function openFilePreview(revision: VaultFileRevisionInDb): void {
  previewRevisionUid.value = revision.uid;
  previewRevisionStatus.value = revision.preview_status ?? null;
  previewFileName.value = revision.file_name;
  if (splitSize.value >= 1) splitSize.value = 0.55;
}

function closeFilePreview(): void {
  previewRevisionUid.value = null;
  previewRevisionStatus.value = null;
  previewFileName.value = '';
  splitSize.value = 1;
}

async function loadRuns(): Promise<void> {
  if (!playbookUid.value) return;
  loading.value = true;
  error.value = null;
  try {
    const result = await PlaybooksService.searchRuns(playbookUid.value, {
      page: currentPage.value,
      pageSize: pageSize.value,
      sortKey: runSortKey.value,
      sortDirection: runSortDirection.value,
    });
    runs.value = result.results;
    totalCount.value = result.result_count;
  } catch (error_) {
    error.value = error_ instanceof Error ? error_.message : 'Failed to load playbook runs.';
  } finally {
    loading.value = false;
  }
}

/** Refetch a single run on a websocket update and patch it into the visible page. */
async function refreshRun(uid: string): Promise<void> {
  try {
    const updated = await PlaybooksService.getRun(uid);
    const index = runs.value.findIndex(run => run.uid === uid);
    if (index !== -1) {
      runs.value.splice(index, 1, updated);
    }
  } catch {
    // Ignore transient refetch errors; the next event or reload will recover.
  }
}

usePlaybookRunWebSocket({
  runUids: () => runs.value.map(run => run.uid),
  onRunUpdated: uid => {
    void refreshRun(uid);
  },
  playbookUid: () => playbookUid.value || null,
  onRunCreated: () => void loadRuns(),
});

async function loadEvaluations(): Promise<void> {
  if (!playbookUid.value) return;
  try {
    evaluations.value = await PlaybooksService.listEvaluations(playbookUid.value);
  } catch {
    evaluations.value = [];
  }
}

// Only used to offer the group filter; failing to load just hides that row.
async function loadEvaluationGroups(): Promise<void> {
  if (!playbookUid.value) return;
  try {
    evaluationGroups.value = await PlaybooksService.listEvaluationGroups(playbookUid.value);
  } catch {
    evaluationGroups.value = [];
  }
}

async function loadPlaybookName(): Promise<void> {
  if (!playbookUid.value) return;
  try {
    const playbook = await PlaybooksService.getById(playbookUid.value);
    playbookName.value = playbook.name;
  } catch {
    playbookName.value = '';
  }
}

async function refreshRuns(): Promise<void> {
  // Jump back to the first page so the freshly started run is visible. When
  // already on page 1 the watcher won't fire, so reload explicitly.
  if (currentPage.value === 1) {
    await loadRuns();
  } else {
    currentPage.value = 1;
  }
}

// Start one run per already-resolved input revision uid (from the file selector
// modal, or the files a drop uploaded).
async function startRunsForRevisions(revisionUids: string[]): Promise<void> {
  if (!playbookUid.value || !revisionUids.length) return;
  starting.value = true;
  const loadingMsg = message.loading('Starting playbook runs…', { duration: 0 });
  let succeeded = 0;
  let failed = 0;
  try {
    for (const revisionUid of revisionUids) {
      try {
        await PlaybooksService.startRun(playbookUid.value, {
          input_vault_file_revision_uid: revisionUid,
          // Empty: every configured action runs.
          skip_action_types: [],
        });
        succeeded += 1;
      } catch {
        failed += 1;
      }
    }
    if (succeeded) {
      message.success(`Started ${succeeded} playbook run${succeeded > 1 ? 's' : ''}.`);
    }
    if (failed) {
      message.error(`Failed to start ${failed} playbook run${failed > 1 ? 's' : ''}.`);
    }
    await refreshRuns();
  } finally {
    loadingMsg.destroy();
    starting.value = false;
  }
}

const dropActions: FileDropAction[] = [
  {
    key: 'start-playbook-run',
    label: 'Start a playbook run',
    description: 'Run this playbook against the dropped file.',
    icon: IconPlayCircle,
    run: async (files, ctx): Promise<void> => {
      ctx.close();
      // The dropped files land in a folder the user picks; cancelling starts nothing.
      const uploaded = await openUploadDialog({ files });
      if (!uploaded) return;
      await startRunsForRevisions(uploaded.map(({ revision }) => revision.uid));
    },
  },
];

async function deleteRun(run: PlaybookRunInDb): Promise<void> {
  try {
    await PlaybooksService.deleteRun(run.uid);
    message.success('Playbook run deleted.');
    // Step back a page if we just removed the only row on a non-first page,
    // otherwise the current page would render empty.
    if (runs.value.length === 1 && currentPage.value > 1) {
      currentPage.value -= 1;
    } else {
      await loadRuns();
    }
  } catch (error_) {
    message.error(error_ instanceof Error ? error_.message : 'Failed to delete playbook run.');
  }
}

/**
 * Per-run delete. `focusRun` falls back to the first run when `focusUid` no
 * longer matches anything, so clearing the id is what moves the pane onto the
 * next run rather than leaving it pointed at one that has just gone.
 */
function deleteFocusRun(): void {
  const run = focusRun.value;
  if (!run) return;
  focusUid.value = null;
  void deleteRun(run);
}

watch(currentPage, () => {
  void loadRuns();
});

watch(pageSize, () => {
  void loadRuns();
});

const paginationConfig = usePaginationConfig(currentPage, pageSize, totalCount);

// The rail pages through the same `currentPage` as the grid's table, so
// switching views keeps the page.
const totalPages = computed<number>(() =>
  Math.max(1, Math.ceil(totalCount.value / pageSize.value))
);

function editPlaybook(): void {
  if (!playbookUid.value) return;
  void router.push(playbookEditRoute(playbookUid.value));
}

function rowProps(row: PlaybookRunInDb): { style: string; onClick: () => void } {
  return {
    style: 'cursor: pointer;',
    onClick: (): void => {
      void router.push({
        name: 'playbook-run-detail',
        params: { uid: playbookUid.value, runUid: row.uid },
      });
    },
  };
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const PROJECT_COLUMN_WIDTH = 160;
const GROUP_ACTIONS_COLUMN_WIDTH = 130;
// Wide enough for its own header, not for the addresses behind the discs.
const STARTED_BY_COLUMN_WIDTH = 120;

// Runs started outside a review project have nothing to show here, so the
// column is dropped when the loaded page has no project at all.
const hasProjects = computed<boolean>(() => runs.value.some(run => run.review_project));

// Nothing to report when no group has a trigger configured.
const hasGroupTriggers = computed<boolean>(() =>
  evaluationGroups.value.some(group => (group.triggers ?? []).length > 0)
);

const columns = computed<DataTableColumns<PlaybookRunInDb>>(() => {
  const base: DataTableColumns<PlaybookRunInDb> = [
    {
      title: 'Status',
      key: 'status',
      width: 120,
      fixed: 'left',
      sorter: true,
      sortOrder: sortOrderFor('status'),
      // The dot carries the state and its hover names it, so the column reads
      // the same as the rail's runs in the Per run view.
      render: row =>
        h(GaiaTooltip, null, {
          trigger: () =>
            h(GaiaStatusIndicator, { status: playbookRunStatusMap[row.status].status }),
          default: () => playbookRunStatusMap[row.status].label,
        }),
    },
    {
      title: 'Input',
      key: 'input',
      width: 180,
      fixed: 'left',
      render: row =>
        h(PlaybookRunInputCell, {
          run: row,
          onViewText: (text: string) => {
            openTextModal(text);
          },
          onViewFile: (revision: VaultFileRevisionInDb) => {
            openFilePreview(revision);
          },
        }),
    },
    {
      title: 'Created',
      key: 'created_at',
      width: 170,
      fixed: 'left',
      sorter: true,
      sortOrder: sortOrderFor('created_at'),
      render: row => formatDate(row.created_at),
    },
    {
      title: 'Started by',
      key: 'started_by',
      width: STARTED_BY_COLUMN_WIDTH,
      fixed: 'left',
      // The disc carries the person and its hover names them, the way the
      // status column's dot does — a column of addresses would otherwise be the
      // widest thing on a row whose subject is the run.
      render: (row): VNode | string => {
        const label = startedByLabel(row);
        if (!label) return '-';
        return h(GaiaTooltip, null, {
          trigger: () => h(GaiaAvatar, { size: 24 }, { default: () => startedByInitials(row) }),
          default: () => label,
        });
      },
    },
  ];

  if (hasGroupTriggers.value) {
    base.push({
      title: 'Group actions',
      key: 'group_actions',
      width: GROUP_ACTIONS_COLUMN_WIDTH,
      fixed: 'left',
      render: row => renderGroupActionsCell(row),
    });
  }

  if (hasProjects.value) {
    base.push({
      title: 'Project',
      key: 'review_project',
      width: PROJECT_COLUMN_WIDTH,
      fixed: 'left',
      ellipsis: { tooltip: true },
      render: row =>
        row.review_project
          ? h(
              RouterLink,
              {
                to: reviewProjectRoute(row.review_project.uid),
                onClick: (e: MouseEvent): void => {
                  e.stopPropagation();
                },
              },
              { default: () => row.review_project?.name }
            )
          : '-',
    });
  }

  const actions: DataTableColumns<PlaybookRunInDb> = [
    {
      title: '',
      key: 'actions',
      width: 60,
      fixed: 'left',
      // The delete button is taller than a text line; GaiaRunsTable uses this
      // hook to sit it on the row's first line with the rest of the cells.
      className: 'runs-actions-cell',
      render: row =>
        h(
          GaiaPopconfirm,
          {
            title: 'Delete this run?',
            confirmLabel: 'Delete run',
            onConfirm: (): void => void deleteRun(row),
          },
          {
            default: () => 'Its evaluation results go with it. The input file is not deleted.',
            trigger: () =>
              h(GaiaDeleteButton, {
                size: 'md',
                subject: 'run',
                onClick: (e: MouseEvent): void => {
                  e.stopPropagation();
                },
              }),
          }
        ),
    },
  ];

  return [...actions, ...base, ...buildEvaluationColumnGroups()];
});

/* ------------------------------------------------------- "Per run" view */

const optionMap = computed(() => buildOptionMap(evaluations.value));

/** Rail label for a run: the input file's name, else the head of its raw text. */
function runInputLabel(run: PlaybookRunInDb): string {
  const fileName = run.input_vault_file_revision?.file_name;
  if (fileName) return fileName;
  const text = runInputText(run);
  if (!text) return '—';
  const firstLine = text.split('\n')[0]?.trim() ?? '';
  if (!firstLine) return 'Text input';
  return firstLine.length > 60 ? `${firstLine.slice(0, 60)}…` : firstLine;
}

/** A result's hover body, or null for a row with nothing more to say than its line. */
interface FocusDetail {
  variant: 'outcome' | 'extraction';
  value: string;
  tone?: GaiaChipTone;
  reasoning?: string;
  actions: GaiaResultAction[];
}

interface FocusRow {
  key: string;
  label: string;
  value: string;
  tone: GaiaResultTone;
  trigger?: GaiaTriggerState;
  detail: FocusDetail | null;
}

interface FocusGroup {
  key: string;
  label: string;
  count: string;
  rows: FocusRow[];
}

/** Trigger uids owned by a group, so a member's rows don't claim the group's actions. */
const groupTriggerUids = computed<Set<string>>(
  () => new Set(groupsByTriggerUid(evaluationGroups.value).keys())
);

/** The pane's result groups — the visible evaluations split by type, in column order. */
const focusGroups = computed<FocusGroup[]>(() => {
  const run = focusRun.value;
  return typeBuckets.value
    .map(bucket => {
      const grouped = visibleEvaluations.value.filter(
        evaluation => evaluation.evaluation_type === bucket.key
      );
      return {
        key: bucket.key,
        label: bucket.label,
        count: String(grouped.length),
        rows: run ? grouped.map(evaluation => focusRow(run, evaluation)) : [],
      };
    })
    .filter(group => group.rows.length > 0);
});

/** Severity drives the value's colour: High reads as alert, Medium as warning. */
const SEVERITY_TONE: Record<SeverityBucket, GaiaResultTone> = {
  High: 'alert',
  Medium: 'warning',
  Low: 'default',
  Other: 'default',
};

/** The same severity as the hover's pill, matching the grid's tag types. */
const SEVERITY_CHIP: Record<SeverityBucket, GaiaChipTone> = {
  High: 'alert',
  Medium: 'warning',
  Low: 'success',
  Other: 'neutral',
};

/** The em-dash the rows and the grid both stand in with when there is no answer. */
const BLANK = '—';

/** A fired action's line reads as text unless its status is worth colouring. */
const ACTION_TONE: Partial<Record<TagType, GaiaResultAction['tone']>> = {
  error: 'alert',
  warning: 'warning',
};

/** One fired action as the hover lists it, in the grid's own action vocabulary. */
function resultAction(action: PlaybookActionResult): GaiaResultAction {
  return {
    key: action.uid,
    label: actionResultTypeLabel(action.action_type),
    detail: actionResultDetail(action) || undefined,
    icon: actionTypeIcon(action.action_type),
    tone: ACTION_TONE[stepStatusTagType(action.status)] ?? 'text',
  };
}

/**
 * A result as the row shows it. Mirrors the grid's cell rendering, flattened:
 * GaiaRunResultRow takes a string, so tags collapse to their label and the risk
 * they carried becomes the row's tone. `detail` is the grid's hover body — it
 * opens once the result has more than its line to give: reasoning, actions that
 * fired, or an answer too long for the row.
 */
function focusRow(run: PlaybookRunInDb, evaluation: PlaybookEvaluation): FocusRow {
  const head = { key: evaluation.uid, label: evaluation.name };
  // The bolt flags a configured trigger before it ever fires, so what actions
  // exist comes from the evaluation, not from the result.
  const configured = evaluationConfiguredActions(evaluation).length > 0;
  const result = latestResultForEvaluation(run, evaluation.uid);
  if (!result) {
    return {
      ...head,
      value: BLANK,
      tone: 'muted',
      trigger: configured ? 'idle' : undefined,
      detail: null,
    };
  }
  const reasoning = result.reasoning ?? undefined;
  const actions = firedEvaluationActions(result, groupTriggerUids.value).map(resultAction);
  const trigger = actions.length > 0 ? 'fired' : configured ? 'idle' : undefined;
  const hasExtra = Boolean(reasoning) || actions.length > 0;

  if (isExtractionResult(result)) {
    const formatted = formatDisplayValue(
      extractedValue(result),
      extractionResponseType(evaluation) ?? undefined
    );
    const empty = formatted.trim().length === 0;
    const value = empty ? BLANK : formatted;
    return {
      ...head,
      value,
      tone: empty ? 'muted' : 'default',
      trigger,
      detail: empty && !hasExtra ? null : { variant: 'extraction', value, reasoning, actions },
    };
  }

  if (isUncommonsResult(result)) {
    const findings = uncommonsFindings(result);
    const bucket = averageFindingBucket(findings);
    const value =
      findings.length === 0 ? BLANK : `${findings.length} finding${findings.length > 1 ? 's' : ''}`;
    return {
      ...head,
      value,
      tone:
        findings.length === 0
          ? 'muted'
          : bucket === 'High' || bucket === 'Medium'
            ? 'warning'
            : 'default',
      trigger,
      detail: hasExtra
        ? {
            variant: 'outcome',
            value,
            tone: findings.length === 0 ? undefined : SEVERITY_CHIP[bucket],
            reasoning,
            actions,
          }
        : null,
    };
  }

  // Classification and clause validation carry no value of their own — the
  // matched option is the answer. Clause validation shows its tier, matching
  // the grid's cell.
  const names = (result.matched_option_uids ?? [])
    .map(uid => {
      const option = optionMap.value.get(uid);
      if (!option) return undefined;
      // Only clause-validation options carry a tier.
      return 'option_type' in option && option.option_type
        ? clauseOptionTypeShortLabel(option.option_type)
        : option.name;
    })
    .filter((name): name is string => Boolean(name));

  const severity = resultSeverity(result, optionMap.value);
  const value = names.length === 0 ? BLANK : names.join(', ');
  return {
    ...head,
    value,
    tone: names.length === 0 ? 'muted' : SEVERITY_TONE[severity],
    trigger,
    detail: hasExtra
      ? {
          variant: 'outcome',
          value,
          tone: names.length === 0 ? undefined : SEVERITY_CHIP[severity],
          reasoning,
          actions,
        }
      : null,
  };
}

/** Pane header line: the run's status plus how much of it landed. */
const focusStatusLine = computed<string>(() => {
  const run = focusRun.value;
  if (!run) return '';
  const label = playbookRunStatusMap[run.status].label;
  const done = (run.evaluation_results ?? []).length > 0;
  return done
    ? `${label} · ${visibleEvaluations.value.length} evaluations`
    : `${label} · no results yet`;
});

const scrollX = computed<number>(() => {
  let fixedWidth = 600 + STARTED_BY_COLUMN_WIDTH;
  if (!hasProjects.value) fixedWidth -= PROJECT_COLUMN_WIDTH;
  if (hasGroupTriggers.value) fixedWidth += GROUP_ACTIONS_COLUMN_WIDTH;
  return fixedWidth + visibleEvaluations.value.length * 200;
});

onMounted(() => {
  void loadPlaybookName();
  void loadEvaluations();
  void loadEvaluationGroups();
  void loadRuns();
});
</script>

<template>
  <GaiaFileDropper :actions="dropActions">
    <Layout :content-max-width="gaiaContentWidth.table">
      <template #breadcrumb>
        <n-breadcrumb-item>{{ navLabels.review }}</n-breadcrumb-item>
        <n-breadcrumb-item clickable>
          <router-link :to="{ name: 'playbooks' }">{{ navLabels.playbooks }}</router-link>
        </n-breadcrumb-item>
        <n-breadcrumb-item>{{ playbookName || 'Runs' }}</n-breadcrumb-item>
      </template>

      <n-flex vertical :size="14">
        <n-flex align="flex-start" justify="space-between" :size="16">
          <n-flex vertical :size="5" :style="{ minWidth: 0 }">
            <gaia-header :level="1" variant="display" style="margin: 0">
              Playbook runs
            </gaia-header>
            <gaia-text truncate size="body" tone="tertiary">
              {{ playbookName ? `Runs for "${playbookName}"` : 'All runs for this playbook.' }}
            </gaia-text>
          </n-flex>
          <n-flex align="center" :size="10" :wrap="false">
            <gaia-button v-if="canEditPlaybook" @click="editPlaybook">
              <template #icon>
                <n-icon :component="IconEdit" />
              </template>
              Edit playbook
            </gaia-button>
            <gaia-button type="primary" :loading="starting" @click="showStartRunModal = true">
              <template #icon>
                <n-icon :component="IconPlayCircle" />
              </template>
              Start run
            </gaia-button>
          </n-flex>
        </n-flex>

        <gaia-banner v-if="error">{{ error }}</gaia-banner>
        <n-flex vertical :size="9">
          <n-flex align="center" justify="space-between" :size="12">
            <n-flex align="center" :size="10">
              <template v-if="typeBuckets.length > 0">
                <gaia-section-header :style="filterLabelStyle"> Type </gaia-section-header>
                <gaia-segmented
                  :value="typeFilter"
                  :options="typeSwitcherOptions"
                  @update:value="value => (typeFilter = value)"
                />
              </template>

              <gaia-text v-if="evalSummary" size="control" tone="subtle">
                {{ evalSummary }}
              </gaia-text>
            </n-flex>

            <n-flex align="center" :size="8" :wrap="false">
              <evaluation-column-picker
                v-if="evaluations.length"
                v-model:hidden="hiddenColumns"
                :evaluations="evaluations"
              />
              <gaia-segmented
                :value="activeTab"
                :options="VIEW_OPTIONS"
                @update:value="value => (activeTab = value)"
              />
            </n-flex>
          </n-flex>

          <n-flex v-if="showGroupFilterRow(groupBuckets)" align="center" :size="10">
            <gaia-section-header :style="filterLabelStyle"> Groups </gaia-section-header>
            <gaia-segmented
              :value="groupFilter"
              :options="groupSwitcherOptions"
              @update:value="value => (groupFilter = value)"
            />
          </n-flex>
        </n-flex>

        <gaia-run-focus v-if="activeTab === 'focus' && runs.length > 0" :style="focusHeightStyle">
          <template #rail-header>
            <gaia-count-label>{{ totalCount }} runs</gaia-count-label>
            <gaia-sort-control
              :options="RUN_SORTS"
              :value="runSortKey"
              :direction="runSortDirection"
              :style="{ marginLeft: 'auto' }"
              @update:value="onRunSortKeyChange"
              @update:direction="onRunSortDirectionChange"
            />
          </template>

          <template #rail>
            <gaia-run-list-item
              v-for="run in runs"
              :key="run.uid"
              :input="runInputLabel(run)"
              :created="formatDate(run.created_at)"
              :started-by="startedByLabel(run) || undefined"
              :started-by-initials="startedByInitials(run)"
              :status="playbookRunStatusMap[run.status].status"
              :status-label="playbookRunStatusMap[run.status].label"
              :selected="focusRun?.uid === run.uid"
              @select="focusUid = run.uid"
            />
          </template>

          <template v-if="totalPages > 1" #rail-footer>
            <gaia-doc-pager
              :page="currentPage"
              :total="totalPages"
              @prev="currentPage -= 1"
              @next="currentPage += 1"
            />
          </template>

          <template v-if="focusRun" #header>
            <n-flex align="center" :size="9" :wrap="false" :style="{ flex: 1, minWidth: 0 }">
              <gaia-tooltip>
                <template #trigger>
                  <gaia-status-indicator :status="playbookRunStatusMap[focusRun.status].status" />
                </template>
                {{ playbookRunStatusMap[focusRun.status].label }}
              </gaia-tooltip>
              <!-- The title is the way into the run's own split-screen detail;
                   a RouterLink so cmd- and middle-click behave. -->
              <router-link
                :to="{
                  name: 'playbook-run-detail',
                  params: { uid: playbookUid, runUid: focusRun.uid },
                }"
                :style="{ minWidth: 0, textDecoration: 'none' }"
              >
                <gaia-text truncate size="body" weight="bold" :style="{ color: gaiaAccent.base }">
                  {{ runInputLabel(focusRun) }}
                </gaia-text>
              </router-link>
            </n-flex>
            <gaia-text size="control" tone="subtle" :style="{ flex: 'none' }">
              {{ focusStatusLine }}
            </gaia-text>
            <gaia-popconfirm
              title="Delete this run?"
              confirm-label="Delete run"
              @confirm="deleteFocusRun"
            >
              <template #trigger>
                <gaia-delete-button size="md" subject="run" :style="{ flex: 'none' }" />
              </template>
              Its evaluation results go with it. The input file is not deleted.
            </gaia-popconfirm>
          </template>

          <n-flex v-if="focusRun" vertical :size="18">
            <div v-for="group in focusGroups" :key="group.key">
              <gaia-section-header variant="rule" :count="group.count">
                {{ group.label }}
              </gaia-section-header>
              <n-grid cols="1 s:2" responsive="screen" :x-gap="28" :style="{ marginTop: '9px' }">
                <n-gi v-for="row in group.rows" :key="row.key">
                  <gaia-run-result-row
                    :label="row.label"
                    :value="row.value"
                    :tone="row.tone"
                    :trigger="row.trigger"
                  >
                    <template v-if="row.detail" #detail>
                      <gaia-run-result-detail
                        :variant="row.detail.variant"
                        :value="row.detail.value"
                        :tone="row.detail.tone"
                        :reasoning="row.detail.reasoning"
                        :actions="row.detail.actions"
                      />
                    </template>
                  </gaia-run-result-row>
                </n-gi>
              </n-grid>
            </div>
          </n-flex>
        </gaia-run-focus>

        <n-flex v-if="showEmptyState" align="center" justify="center" :style="focusHeightStyle">
          <gaia-empty-state title="No runs yet">
            <template #icon>
              <n-icon :component="IconPlayCircle" :size="18" />
            </template>
            Start a run to check documents against this playbook’s
            {{ evaluations.length }} evaluations. Results land here, one row per input.
            <template #actions>
              <gaia-button type="primary" @click="showStartRunModal = true">
                <template #icon>
                  <n-icon :component="IconPlayCircle" />
                </template>
                Start run
              </gaia-button>
            </template>
          </gaia-empty-state>
        </n-flex>

        <n-split
          v-if="showGrid"
          v-model:size="splitSize"
          :style="{ height: splitHeight }"
          :min="0.3"
          :max="0.85"
          :disabled="previewRevisionUid === null"
          :resize-trigger-size="previewRevisionUid === null ? 0 : 4"
        >
          <template #1>
            <div style="height: 100%">
              <gaia-runs-table
                data-tour="pb.runs-table"
                :columns="columns"
                :ellipsis-header="false"
                :data="runs"
                :loading="loading"
                :pagination="runs.length > 0 ? paginationConfig : false"
                :remote="true"
                :scroll-x="scrollX"
                :row-key="(row: PlaybookRunInDb) => row.uid"
                :row-props="rowProps"
                flex-height
                :style="{ height: '100%' }"
                @update:sorter="onSorterChange"
              >
              </gaia-runs-table>
            </div>
          </template>
          <template #2>
            <n-flex
              v-if="previewRevisionUid"
              vertical
              :size="12"
              style="height: 100%; padding-left: 12px"
            >
              <n-flex align="center" justify="space-between" :size="8">
                <gaia-text truncate size="body" weight="semibold" tone="ink">
                  {{ previewFileName || 'File preview' }}
                </gaia-text>
                <gaia-icon-button
                  :dimension="28"
                  quaternary
                  aria-label="Close preview"
                  @click="closeFilePreview"
                >
                  <n-icon :component="IconClose" :size="14" />
                </gaia-icon-button>
              </n-flex>
              <div style="flex: 1; min-height: 0">
                <gaia-vault-file-pdf-preview
                  :revision-uid="previewRevisionUid"
                  :revision-preview-status="previewRevisionStatus"
                />
              </div>
            </n-flex>
          </template>
        </n-split>

        <n-modal
          v-model:show="showTextModal"
          preset="card"
          title="Input text"
          style="width: min(720px, calc(100vw - 32px))"
        >
          <pre
            style="
              margin: 0;
              max-height: 60vh;
              overflow: auto;
              white-space: pre-wrap;
              word-break: break-word;
              font-size: 13px;
              line-height: 1.6;
            "
            >{{ textModalContent }}</pre>
        </n-modal>
      </n-flex>
    </Layout>

    <template #overlay>
      <n-flex
        vertical
        align="center"
        justify="center"
        :size="12"
        :style="{ color: themeVars.primaryColor, fontSize: '18px', fontWeight: 600 }"
      >
        <n-icon :size="40"><IconPlayCircle /></n-icon>
        <p style="margin: 0">Drop files to start a playbook run for each</p>
      </n-flex>
    </template>
  </GaiaFileDropper>

  <PlaybookStartRunModal v-model:show="showStartRunModal" @confirm="startRunsForRevisions" />
</template>
